<template>
  <div style="position: relative;" class="aws-table-container">
    <v-data-table :headers="headers" :items="data" hide-actions :loading="loading" :custom-sort="sortByNum"
      :pagination.sync="localPagination" @update:pagination="$emit('update:pagination', $event)" must-sort class="primary home-table">
    <template v-slot:items="props">
      <v-tooltip top class="expire-tooltip-wrapper"
        :content-class="(isNearingRenew(props.item.plant_id) || isRenewExceeded(props.item.plant_id)) ? 'custom-tooltip expire-tooltip' : 'custom-tooltip'"
        :disabled="isPlantExpired(props.item.plant_id) || (!isNearingRenew(props.item.plant_id) && !isRenewExceeded(props.item.plant_id))">
        <template v-slot:activator="{ on }">
          <tr v-on="on" :class="highlight(props.item.plant_id)"
            @click="!resizeState.wasResizing && $emit('row-clicked', props.item)" class="primaryText--text">
            <td v-for="(header, colIndex) in headers" :key="header.value"
              :class="cellClasses(header.value, props.item)"
              :id="header.value">
              <template v-if="header.value === 'plant_name' || header.value === 'location'">
                <v-tooltip top content-class="custom-tooltip">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ props.item[header.value] }}</span>
                  </template>
                  <span>{{ props.item[header.value] }}</span>
                </v-tooltip>
              </template>

              <template v-else-if="isNumericColumn(header.value)">
                <span v-if="!isPlantExpired(props.item.plant_id)">
                  {{ formatCellValue(header.value, props.item) }}
                </span>
              </template>

              <template v-else-if="header.value === 'ac_capacity'">
                <span>{{ props.item.ac_capacity }}</span>
              </template>

              <template v-else-if="header.value === 'alert'">
                <span v-if="!isPlantExpired(props.item.plant_id)">
                  <span v-for="(value, key) in props.item.alert" v-bind:key="key">
                    <AlertIndicator :alertTag="alertShorthand(key)" :tip="key.toUpperCase()" :alert="value === 'alert'"
                      @click="
                        $emit('alert-action', {
                          plantId: props.item.plant_id,
                          alert: key,
                        })
                        "></AlertIndicator>
                  </span>
                </span>
              </template>

            </td>
          </tr>
        </template>
        <div class="text-center pa-1">
          <p class="font-weight-bold mb-2">{{ props.item.plant_name }}</p>
          <p v-if="getExpiryDateCount(props.item.plant_id) && getExpiryDateCount(props.item.plant_id) > 0"
            class="renew-msg mb-1">
            {{ getExpiryDateCount(props.item.plant_id) }} days to Expire
          </p>
          <p v-if="getRenewDate(props.item.plant_id)" class="renew-msg mb-0">
            Renew till : {{ getRenewDate(props.item.plant_id) }}
          </p>
        </div>
      </v-tooltip>
    </template>
    <template v-slot:headers="props">
      <tr>
        <th
          v-for="(header) in props.headers"
          :key="header.value"
          :class="[
            'column',
            header.value !== 'alert' ? 'sortable' : '',
            localPagination.sortBy === header.value ? 'active' : '',
            localPagination.sortBy === header.value && localPagination.descending ? 'desc' : 'asc'
          ]"
          style="position: sticky; top: 0; z-index: 2; padding: 0;"
        >
          <div style="display: flex; height: 48px; align-items: center; justify-content: space-between;">
            <div 
              style="flex-grow: 1; display: flex; align-items: center; justify-content: flex-start; height: 100%; padding: 0 15px;"
              @click="header.value !== 'alert' ? changeSort(header.value) : null"
              :style="{ cursor: header.value !== 'alert' ? 'pointer' : 'default' }"
            >
              <span>{{ header.text }}</span>
              <v-icon
                v-if="header.value !== 'alert'"
                small
                class="ml-1 aws-sort-icon"
                :style="{ opacity: localPagination.sortBy === header.value ? 1 : 0.3 }"
              >
                arrow_upward
              </v-icon>
            </div>
            
              <div 
                style="width: 10px; cursor: col-resize; position: absolute; right: -5px; top: 0; bottom: 0; z-index: 3;"
                class="aws-resize-handle"
                @mouseenter="handleHoverEnter"
                @mouseleave="handleHoverLeave"
                @click.stop.prevent
                @mousedown.stop.prevent="startResize(header, $event)"
              ></div>
          </div>
        </th>
      </tr>
    </template>
  </v-data-table>
  
  <div
    v-show="resizeState.active || resizeState.isHovering"
    style="position: absolute; top: 0; bottom: 0; width: 2px; pointer-events: none; z-index: 15;"
    :style="{ 
      left: (resizeState.active ? resizeState.lineX : resizeState.hoverLineX) + 'px',
      backgroundColor: resizeState.active ? '#5b9aff' : 'rgba(91, 154, 255, 0.3)',
      boxShadow: resizeState.active ? '0 0 6px rgba(91, 154, 255, 0.7)' : 'none'
    }"
  ></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mixin } from "@/util/mixin/index";

import AlertIndicator from "@/components/home/DataTable/AlertIndicator";

export default {
  props: {
    headers: {
      type: Array,
      default: function () {
        return [];
      },
    },
    data: {
      type: Array,
      default: function () {
        return [];
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
  },
  mixins: [mixin],
  components: {
    AlertIndicator,
  },
  data() {
    return {
      localPagination: Object.assign({}, this.pagination),
      resizeState: {
        active: false,
        wasResizing: false,
        header: null,
        startX: 0,
        startWidth: 0,
        colElement: null,
        tableElement: null,
        startTableWidth: 0,
        newWidth: null,
        lineX: 0,
        hoverLineX: 0,
        isHovering: false
      }
    };
  },
  computed: {
    ...mapGetters([
      "isNearingRenew",
      "isRenewExceeded",
      "isPlantExpired",
      "getExpiryDateCount",
      "getRenewDate",
    ]),
  },
  mounted() {
    this.$nextTick(() => {
      this.injectColGroup();
    });
  },
  updated() {
    this.$nextTick(() => {
      const table = this.$el.querySelector('table');
      if (table && !table.querySelector('colgroup.resize-colgroup')) {
        this.injectColGroup();
      }
    });
  },
  beforeDestroy() {
    if (this.resizeState.active) {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.cursor = '';
    }
  },
  watch: {
    pagination: {
      handler(newVal) {
        this.localPagination = Object.assign({}, newVal);
      },
      deep: true
    }
  },
  methods: {
    changeSort(columnValue) {
      let isDesc = false;
      if (this.localPagination.sortBy === columnValue) {
        isDesc = !this.localPagination.descending;
      }
      
      this.localPagination = Object.assign({}, this.localPagination, {
        sortBy: columnValue,
        descending: isDesc
      });

      this.$emit('update:pagination', this.localPagination);
    },
    injectColGroup() {
      const table = this.$el.querySelector("table");
      if (!table) return;

      const ths = table.querySelectorAll("thead th");
      const currentWidths = Array.from(ths).map((th) => th.offsetWidth);

      const existing = table.querySelector("colgroup.resize-colgroup");
      if (existing) existing.remove();

      const colgroup = document.createElement("colgroup");
      colgroup.className = "resize-colgroup";

      const fragment = document.createDocumentFragment();
      this.headers.forEach((header, idx) => {
        const col = document.createElement("col");
        col.dataset.colValue = header.value;

        if (header.width) {
          col.style.width = parseInt(header.width) + "px";
        } else if (currentWidths[idx] > 0) {
          col.style.width = currentWidths[idx] + "px";
        }
        fragment.appendChild(col);
      });
      colgroup.appendChild(fragment);
      table.insertBefore(colgroup, table.firstChild);

      const cols = colgroup.querySelectorAll("col");
      let sumWidth = 0;
      cols.forEach((col) => {
        sumWidth += parseInt(col.style.width) || 0;
      });

      const container = table.parentElement;
      const containerWidth = container ? container.clientWidth : 0;
      const hasPersistedWidths = this.headers.some((h) => h.width);

      if (containerWidth > 0 && cols.length > 0) {
        const lastCol = cols[cols.length - 1];
        const lastColWidth = parseInt(lastCol.style.width) || 0;
        if (sumWidth < containerWidth) {
          const adjustedWidth = lastColWidth + (containerWidth - sumWidth);
          lastCol.style.width = adjustedWidth + "px";
          sumWidth = containerWidth;
        } else if (sumWidth > containerWidth && !hasPersistedWidths) {
          const adjustedWidth = Math.max(60, lastColWidth - (sumWidth - containerWidth));
          lastCol.style.width = adjustedWidth + "px";
          sumWidth = containerWidth;
        }
      }

      if (sumWidth > 0) {
        table.style.width = sumWidth + "px";
      }
    },
    startResize(header, event) {
      const table = this.$el.querySelector('table');
      if (!table) return;

      let colgroup = table.querySelector('colgroup.resize-colgroup');
      if (!colgroup) {
        this.injectColGroup();
        colgroup = table.querySelector('colgroup.resize-colgroup');
        if (!colgroup) return;
      }

      const col = colgroup.querySelector(`col[data-col-value="${header.value}"]`);
      if (!col) return;

      const ths = table.querySelectorAll('thead th');

      const cols = colgroup.querySelectorAll('col');
      cols.forEach((c, i) => {
        const thEl = ths[i];
        if (thEl) {
          c.style.width = thEl.offsetWidth + 'px';
          c.style.minWidth = '';
        }
      });

      let calculatedTableWidth = 0;
      cols.forEach(c => {
        calculatedTableWidth += parseInt(c.style.width) || 0;
      });

      const container = table.parentElement;
      const containerWidth = container ? container.clientWidth : 0;
      if (calculatedTableWidth < containerWidth && cols.length > 0) {
        const lastCol = cols[cols.length - 1];
        if (lastCol) {
          const lastColWidth = parseInt(lastCol.style.width) || 0;
          lastCol.style.width = (lastColWidth + (containerWidth - calculatedTableWidth)) + 'px';
          calculatedTableWidth = containerWidth;
        }
      }
      table.style.width = calculatedTableWidth + 'px';

      const startWidth = parseInt(col.style.width) || 100;

      this.resizeState.colElement = col;
      this.resizeState.startWidth = startWidth;
      this.resizeState.header = header;
      this.resizeState.startX = event.clientX;
      this.resizeState.active = true;
      this.resizeState.newWidth = null;
      this.resizeState.tableElement = table;
      this.resizeState.startTableWidth = calculatedTableWidth;
      
      const containerRect = this.$el.getBoundingClientRect();
      this.resizeState.lineX = event.clientX - containerRect.left;

      this.resizeState.handle = event.target;
      this.resizeState.handle.classList.add('active');

      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      document.body.style.cursor = 'col-resize';

      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    },
    onMouseMove(event) {
      if (!this.resizeState.active) return;

      const diff = event.clientX - this.resizeState.startX;
      let newWidth = this.resizeState.startWidth + diff;
      newWidth = Math.max(newWidth, 60);
      this.resizeState.newWidth = newWidth;

      const containerRect = this.$el.getBoundingClientRect();
      this.resizeState.lineX = event.clientX - containerRect.left;

      if (this.resizeState.colElement) {
        this.resizeState.colElement.style.width = newWidth + 'px';
      }

      if (this.resizeState.tableElement && this.resizeState.startTableWidth) {
        const widthDelta = newWidth - this.resizeState.startWidth;
        this.resizeState.tableElement.style.width =
          (this.resizeState.startTableWidth + widthDelta) + 'px';
      }
    },
    onMouseUp() {
      if (!this.resizeState.active) return;

      if (this.resizeState.handle) {
        this.resizeState.handle.classList.remove('active');
        this.resizeState.handle = null;
      }

      this.resizeState.active = false;
      this.resizeState.wasResizing = true;

      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.cursor = '';

      setTimeout(() => {
        this.resizeState.wasResizing = false;
      }, 0);

      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);

      if (this.resizeState.newWidth && this.resizeState.header) {
        this.$emit('update-col-width', { value: this.resizeState.header.value, width: this.resizeState.newWidth });
      }
    },
    handleHoverEnter(event) {
      if (this.resizeState.active) return;
      const containerRect = this.$el.getBoundingClientRect();
      const handleRect = event.target.getBoundingClientRect();
      this.resizeState.hoverLineX = handleRect.left + (handleRect.width / 2) - containerRect.left;
      this.resizeState.isHovering = true;
    },
    handleHoverLeave() {
      this.resizeState.isHovering = false;
    },
    alertShorthand(device) {
      return device.charAt(0).toUpperCase();
    },
    isNumericColumn(col) {
      return ['generation', 'power', 'acyield', 'cuf', 'ratio', 'albedoCorrPr', 'tempCorrPr'].includes(col);
    },
    cellClasses(col, item) {
      const classes = [];
      if (col !== 'alert') classes.push('overflowtext');

      if (this.isNumericColumn(col)) {
        const val = item[col];
        const hasData = val != null && val !== 'No data';
        classes.push(hasData ? 'number--text' : 'error--text');
      } else if (col === 'ac_capacity') {
        classes.push('number--text');
      }

      return classes;
    },
    formatCellValue(col, item) {
      const val = item[col];
      
      if (val == null || val === 'No data') return this.$t('No data');
      if (col === 'generation') return this.$options.filters.numberWithCommas(val);
      if (col === 'ratio' && val > 100) return '---';
      if (['acyield', 'cuf', 'ratio', 'albedoCorrPr', 'tempCorrPr'].includes(col)) return this.$options.filters.round(val);
      
      return val;
    },
    sortByNum(item, index, isDesc) {
      const getNum = (v) => parseFloat((v || 0).toString().replace(/,/g, "")) || 0;

      const compare = (a, b) => {
        let valA, valB;

        if (index === "generation" || index === "ac_capacity" || index === "power") {
          valA = getNum(a[index]);
          valB = getNum(b[index]);
        } else if (index === "plant_name" || index === "location") {
          valA = a[index] || "";
          valB = b[index] || "";
        } else if (index === "ratio") {
          if (typeof a[index] !== typeof b[index]) {
            return isDesc 
              ? (typeof a[index] > typeof b[index] ? -1 : 1)
              : (typeof a[index] < typeof b[index] ? -1 : 1);
          }
          valA = a[index];
          valB = b[index];
        } else {
          return 0;
        }

        if (valA < valB) return isDesc ? 1 : -1;
        if (valA > valB) return isDesc ? -1 : 1;
        return 0;
      };

      return item.sort(compare);
    },
    highlight(plantId) {
      const renew = this.isNearingRenew(plantId);
      const exceededRenew = this.isRenewExceeded(plantId);
      const expired = this.isPlantExpired(plantId);
      const className = renew || exceededRenew || expired ? "con-tooltip" : "";
      return `${className} top`;
    },
    toolTip(plantId) {
      return this.isNearingRenew(plantId) || this.isRenewExceeded(plantId) || this.isPlantExpired(plantId);
    },
  },
  watch: {
    pagination: {
      handler(val) {
        this.localPagination = val;
      },
      deep: true,
    },
    localPagination: {
      handler(val) {
        this.$emit("update:pagination", val);
      },
      deep: true,
    },
    headers: {
      handler() {
        this.$nextTick(() => {
          this.injectColGroup();
        });
      },
      deep: true
    }
  }
};
</script>

<style>
.home-table {
  margin: 0 2px;
  height: calc(100vh - 34vh);
  display: flex;
}

.home-table .v-table tbody td {
  height: 42px;
}

.custom-tooltip {
  background-color: var(--v-primary-base) !important;
  color: var(--v-extraGrey-base);
  border-radius: 4px;
  border: 1px solid rgba(83, 122, 228, 0.418);
  z-index: 16 !important;
}

.expire-tooltip {
  background-color: rgba(204, 83, 86, 0.95) !important;
  color: white !important;
  border-color: rgba(204, 83, 86, 0.95) !important;
}

.con-tooltip {
  background: #cc535673;
  cursor: pointer;
}

.renew-msg {
  margin-bottom: 6px;
  margin-top: 6px;
}

.home-table th {
  position: sticky;
  z-index: 2;
  top: 0;
  color: var(--v-extraGrey-base);
  font-weight: 500 !important;
  cursor: pointer !important;
}

.home-table .v-table thead th.column.sortable {
  cursor: pointer !important;
}

.home-table th.col-resize-hover,
.home-table th.col-resize-hover:hover {
  color: var(--v-extraGrey-base) !important;
}

.home-table th.col-resize-hover .v-icon {
  display: none !important;
}

.home-table td {
  cursor: default;
}

.col-resizer {
  position: absolute;
  top: 0;
  right: -4px;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 15;
  transition: background-color 0.15s ease;
}

.col-resizer.col-resizer-left {
  right: auto;
  left: -4px;
}

td .col-resizer:not(.col-resizer-left):hover::after,
td .col-resizer:not(.col-resizer-left).resizer-hovered::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background-color: #4a7fd4;
}

.col-resizer.col-resizer-left:hover::after,
.col-resizer.col-resizer-left.resizer-hovered::after {
  display: none;
}

th .col-resizer:hover::after,
th .col-resizer.resizer-hovered::after,
th .col-resizer.col-resizer-left:hover::after,
th .col-resizer.col-resizer-left.resizer-hovered::after {
  display: none !important;
}

.home-table th.col-resize-hover-owner {
  box-shadow: inset -2px 0 0 #4a7fd4;
}

td .col-resizer:not(.col-resizer-left).resizer-active::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background-color: #5b9aff;
  box-shadow: 0 0 6px rgba(91, 154, 255, 0.7);
}

td .col-resizer.col-resizer-left.resizer-active::after {
  display: none;
}

th .col-resizer.resizer-active::after,
th .col-resizer.col-resizer-left.resizer-active::after {
  display: none !important;
}

.home-table .v-table__overflow {
  overflow: auto;
}

.home-table table {
  table-layout: fixed !important;
  border-collapse: collapse;
  border: 1px solid #38405C;
}

.home-table .v-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  border-right: 1px solid #38405C !important;
  background: var(--v-primary-base);
  border-top: 1px solid #38405C;
  border-bottom: 2px solid #38405C !important;
}

.aws-resize-handle:hover::after,
.aws-resize-handle.active::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 4px;
  width: 2px;
  background-color: #5b9aff;
  box-shadow: 0 0 6px rgba(91, 154, 255, 0.7);
  z-index: 3;
}

.home-table th,
.home-table td {
  border-right: 1px solid #38405C !important;
  border-bottom: 1px solid #38405C !important;
  padding: 0 15px !important;
}

.home-table td {
  position: relative;
}

.home-table th:last-child,
.home-table td:last-child {
  padding-right: 0 !important;
}

.overflowtext {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1200px) and (min-width: 550px) {
  .home-table {
    height: calc(100vh - 350px);
  }
}

@media (max-width: 1240px) {
  .home-table td:last-child {
    width: 600px;
  }
}
</style>